import hexToRgba from 'hex-to-rgba';
import isEqual from 'lodash/isEqual';
import { ReactNode } from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';

import { Text } from '../components/Text';
import { View } from '../components/View';
import { AccessibleInput } from '../hooks/useAccessibleInput';
import { useTheme } from '../styles';

type Props<T> = {
  disabled?: boolean;
  items: { label: string; value: T; key?: string }[];
  onChangeValue?: (value: T) => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  value?: T | null;
} & (
  | {
      label: string;
      'aria-label'?: string;
    }
  | {
      label?: () => ReactNode;
      'aria-label': string;
    }
);

export function PillGroup<T>({ disabled, items, onChangeValue, value, ...props }: Props<T>) {
  const { theme } = useTheme();

  return (
    <AccessibleInput {...props}>
      {(accessibleProps) => (
        <View
          {...accessibleProps}
          role="radiogroup"
          spacing={15}
          row
          style={{ justifyContent: 'space-evenly' }}
        >
          {items.map((item) => {
            const selected = isEqual(value, item.value);
            return (
              <TouchableOpacity
                testID={props.testID ? `${props.testID}_${item.value}` : undefined}
                key={item.key || item.label}
                disabled={disabled}
                role="radio"
                onPress={() => onChangeValue?.(item.value)}
                aria-selected={selected}
                style={[
                  { paddingHorizontal: 14, paddingVertical: 12, borderRadius: 10 },
                  selected ? { backgroundColor: hexToRgba(theme.color.primary300, 0.3) } : null,
                ]}
              >
                <Text
                  text={item.label}
                  weight="semibold"
                  size={15}
                  color={selected ? theme.color.primary100 : theme.color.gray400}
                />
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </AccessibleInput>
  );
}
